import { isUsableImageUrl } from './itineraryItemImage';

const HEX = /^#([0-9a-fA-F]{6})$/;

export const BRAND_CSS_VARS = {
  canvas: '--dvn-canvas',
  ink: '--dvn-ink',
  oxblood: '--dvn-oxblood',
  accent: '--dvn-accent',
  nav: '--dvn-nav',
  navAccent: '--dvn-nav-accent',
  navInk: '--dvn-nav-ink',
};

export const parseHexColor = (value) => {
  const match = String(value || '').trim().match(HEX);
  return match ? `#${match[1].toUpperCase()}` : null;
};

export const hexLuminance = (hex) => {
  const parsed = parseHexColor(hex);
  if (!parsed) return 0;
  const n = Number.parseInt(parsed.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
};

const HOUSE_NAV_ACCENT = '#C9A961';
const DEFAULT_BANNER = '#000000';
const DEFAULT_NAV_INK = '#F6EFE4';
const DEFAULT_INK = '#000000';
const DEFAULT_CANVAS = '#FFFFFF';
const HOUSE_FALLBACK_COLORS = new Set(['#4A0E15', '#320910']);

const isCustomThemeColor = (hex) => Boolean(hex) && !HOUSE_FALLBACK_COLORS.has(hex);

export const isPartnerWorkspaceBranded = (entitlements) => (
  entitlements?.tier === 'bespoke'
);

export const hasStoredPartnerBranding = (partner) => (
  Boolean(
    parseHexColor(partner?.brand_color)
    || parseHexColor(partner?.brand_banner)
    || parseHexColor(partner?.brand_nav_ink)
    || parseHexColor(partner?.brand_ink)
    || parseHexColor(partner?.brand_canvas)
    || isUsableImageUrl(partner?.logo)
  )
);

const defaultBanner = (accent) => (
  accent && hexLuminance(accent) < 0.35 ? accent : DEFAULT_BANNER
);

/**
 * Custom workspace chrome only when Bespoke has actually set a mark.
 * Agency white-label and an empty Bespoke profile stay house oxblood.
 */
export const getPartnerWorkspaceBrand = (partner, entitlements) => {
  if (!isPartnerWorkspaceBranded(entitlements) || !hasStoredPartnerBranding(partner)) {
    return null;
  }

  const accent = parseHexColor(partner?.brand_color);
  const banner = parseHexColor(partner?.brand_banner) || defaultBanner(accent);

  return {
    name: partner?.company_name || '',
    logoUrl: isUsableImageUrl(partner?.logo) ? String(partner.logo).trim() : null,
    accent: accent || HOUSE_NAV_ACCENT,
    banner,
    header: banner,
    navInk: parseHexColor(partner?.brand_nav_ink) || DEFAULT_NAV_INK,
    ink: parseHexColor(partner?.brand_ink) || DEFAULT_INK,
    canvas: parseHexColor(partner?.brand_canvas) || DEFAULT_CANVAS,
    showHouseCredit: true,
  };
};

export const cssVarsFromWorkspaceBrand = (brand) => {
  if (!brand) return null;
  return {
    canvas: brand.canvas,
    ink: brand.ink,
    oxblood: brand.accent,
    accent: brand.accent,
    nav: brand.banner,
    navAccent: brand.accent,
    navInk: brand.navInk,
  };
};

export const clientBrandingAppliesPageTheme = (branding) => {
  if (!branding || branding.level === 'house') return false;
  if (branding.applyTheme) return true;
  return Boolean(branding.logoUrl) && branding.level && branding.level !== 'co-branded';
};

export const headerBgFromClientBranding = (branding) => {
  const themed = cssVarsFromClientBranding(branding);
  if (themed?.nav) return themed.nav;
  if (!branding?.level || branding.level === 'house') return null;
  return parseHexColor(branding.banner) || parseHexColor(branding.color) || null;
};

export const cssVarsFromClientBranding = (branding) => {
  if (!clientBrandingAppliesPageTheme(branding)) return null;
  const rawAccent = parseHexColor(branding.color || branding.accent);
  const rawBanner = parseHexColor(branding.banner);
  const accent = isCustomThemeColor(rawAccent) ? rawAccent : HOUSE_NAV_ACCENT;
  const banner = isCustomThemeColor(rawBanner) ? rawBanner : defaultBanner(accent);
  return {
    canvas: parseHexColor(branding.canvas) || DEFAULT_CANVAS,
    ink: parseHexColor(branding.ink) || DEFAULT_INK,
    oxblood: accent,
    accent,
    nav: banner,
    navAccent: accent,
    navInk: parseHexColor(branding.navInk) || DEFAULT_NAV_INK,
  };
};

export const applyBrandCssVars = (vars) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  Object.entries(BRAND_CSS_VARS).forEach(([key, name]) => {
    if (vars?.[key]) {
      root.style.setProperty(name, vars[key]);
    } else {
      root.style.removeProperty(name);
    }
  });
};
